/**
 * Unified Messages Inbox — merges the coach-inbox and buddy-DM lists into
 * one screen so users have a single place to check messages.
 *
 * Data flow:
 *   • GET /messages                       → coach threads
 *   • GET /direct-messages/conversations  → buddy 1:1 DMs
 * Normalized shape per entry:
 *   { id, source: 'coach' | 'buddy', title, preview, timestamp,
 *     unread, avatar?, badges?, onOpen() }
 *
 * A buddy convo shows an Users2 icon (peer-to-peer) so it's visually
 * distinct from coach conversations (Shield icon). Buddy convos deep-link
 * to the existing /dm/:userId page; coach threads open the reply modal in
 * place (preserved from the old MessagesPage).
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Mail, MailOpen, Video, Play, ChevronRight, Loader2, ArrowLeft,
  Send, User, Shield, Users2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUtils';
import { NavBar } from '../components/NavBar';

const API = process.env.REACT_APP_BACKEND_URL;

// --- Formatters ---------------------------------------------------------
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'long' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatFullDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

// --- Filter tabs --------------------------------------------------------
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'coach', label: 'Coach' },
  { key: 'buddy', label: 'Buddies' },
];

export const MessagesPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // Coach-thread modal state (buddy threads use the /dm/:userId page)
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const threadEndRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Fire both requests in parallel; degrade gracefully if either fails.
      const [coachRes, buddyRes] = await Promise.allSettled([
        api().get('/messages'),
        api().get('/direct-messages/conversations'),
      ]);

      const coachItems = (coachRes.status === 'fulfilled'
        ? (coachRes.value.data.messages || [])
        : []
      ).map((c) => ({
        id: `coach-${c.id}`,
        source: 'coach',
        raw: c,
        title: c.subject || 'Coach',
        preview: c.latest_content || '',
        latestSender: c.latest_sender,
        timestamp: c.last_activity || c.created_at,
        unread: !!c.unread,
        video: !!c.video_url,
        replyCount: c.reply_count || 0,
      }));

      const buddyItems = (buddyRes.status === 'fulfilled'
        ? (buddyRes.value.data.conversations || [])
        : []
      ).map((c) => ({
        id: `buddy-${c.user.id}`,
        source: 'buddy',
        raw: c,
        title: c.user.name || 'Friend',
        preview: (c.last_message.is_from_me ? 'You: ' : '') + (c.last_message.content || ''),
        timestamp: c.last_message.created_at,
        unread: (c.unread_count || 0) > 0,
        unreadCount: c.unread_count || 0,
        avatar: c.user.profile_picture,
        userId: c.user.id,
      }));

      const merged = [...coachItems, ...buddyItems].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );

      if (!cancelled) {
        setConversations(merged);
        setLoading(false);
      }

      if (coachRes.status === 'rejected' && buddyRes.status === 'rejected') {
        toast.error('Failed to load messages');
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const openConversation = async (item) => {
    if (item.source === 'buddy') {
      navigate(`/dm/${item.userId}`);
      return;
    }
    // Coach — open the reply modal in place.
    setSelectedConversation(item);
    setLoadingThread(true);
    setReplyContent('');
    try {
      const res = await api().get(`/messages/${item.raw.id}/thread`);
      setThread(res.data.thread || []);
      setConversations((prev) => prev.map((c) => (c.id === item.id ? { ...c, unread: false } : c)));
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {
      toast.error('Failed to load conversation');
    }
    setLoadingThread(false);
  };

  const sendReply = async () => {
    if (!replyContent.trim() || !selectedConversation) return;
    setSendingReply(true);
    try {
      await api().post('/messages/reply', {
        conversation_id: selectedConversation.raw.id,
        content: replyContent,
      });
      setThread((prev) => [...prev, {
        id: Date.now().toString(),
        sender_type: 'user',
        sender_name: 'You',
        content: replyContent,
        created_at: new Date().toISOString(),
      }]);
      setReplyContent('');
      toast.success('Reply sent!');
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {
      toast.error('Failed to send reply');
    }
    setSendingReply(false);
  };

  const filtered = conversations.filter((c) => filter === 'all' || c.source === filter);
  const counts = {
    all: conversations.length,
    coach: conversations.filter((c) => c.source === 'coach').length,
    buddy: conversations.filter((c) => c.source === 'buddy').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black" data-testid="messages-page">
        <NavBar title="Messages" />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="messages-page">
      <NavBar title="Messages" />
      <main className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-1">
              Your <span className="text-primary">Messages</span>
            </h2>
            <p className="text-muted-foreground text-sm">One inbox for your coach and buddies.</p>
          </motion.div>

          {/* Filter pills */}
          <div className="flex gap-2 mb-5">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                data-testid={`messages-filter-${key}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-heading uppercase tracking-[0.16em] transition-colors border ${
                  filter === key
                    ? 'bg-primary text-black border-primary'
                    : 'bg-card text-white/70 border-border hover:border-primary/50 hover:text-white'
                }`}
              >
                {label} <span className="opacity-60 ml-1">{counts[key]}</span>
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-card border border-border rounded-2xl p-10 text-center"
              data-testid="messages-empty"
            >
              <Mail className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-2">No Messages Yet</h3>
              <p className="text-muted-foreground text-sm">
                You&apos;ll see coach threads and buddy DMs here as they come in.
              </p>
            </motion.div>
          ) : (
            <div className="space-y-2">
              {filtered.map((c, i) => (
                <motion.button
                  key={c.id}
                  type="button"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i, 8) * 0.03 }}
                  onClick={() => openConversation(c)}
                  data-testid={`conversation-${i}`}
                  className={`w-full text-left bg-card border rounded-2xl p-3 flex items-center gap-3 transition-colors hover:border-primary/50 ${
                    c.unread ? 'border-primary/30 bg-primary/[0.04]' : 'border-border'
                  }`}
                >
                  {/* Source-tagged avatar */}
                  {c.source === 'buddy' ? (
                    <div className="relative shrink-0">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={getImageUrl(c.avatar)} />
                        <AvatarFallback className="bg-primary/20 text-primary text-sm">
                          {c.title.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      {/* Buddy corner badge — makes source obvious at a glance */}
                      <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-blue-500 border-2 border-black flex items-center justify-center" title="Buddy conversation">
                        <Users2 className="h-2.5 w-2.5 text-white" strokeWidth={2.5} />
                      </span>
                    </div>
                  ) : (
                    <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center ${
                      c.unread ? 'bg-primary/20' : 'bg-white/10'
                    }`}>
                      <Shield className={`h-4 w-4 ${c.unread ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`font-heading text-sm uppercase tracking-wider truncate ${
                        c.unread ? 'text-white' : 'text-white/80'
                      }`}>
                        {c.title}
                      </span>
                      {c.source === 'coach' && (
                        <Badge className="bg-primary/15 text-primary text-[9px] uppercase tracking-widest px-1.5 py-0 h-4 border-primary/30">
                          Coach
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-xs truncate">
                      {c.source === 'coach' && c.latestSender === 'user' && 'You: '}
                      {c.preview || <span className="italic">No messages yet</span>}
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-muted-foreground text-[11px] whitespace-nowrap">
                      {formatDate(c.timestamp)}
                    </span>
                    <div className="flex items-center gap-1">
                      {c.video && (
                        <Badge className="bg-blue-500/20 text-blue-400 text-[9px] uppercase px-1.5 py-0 h-4">
                          <Video className="h-2.5 w-2.5 mr-0.5" /> Video
                        </Badge>
                      )}
                      {c.replyCount > 0 && c.source === 'coach' && (
                        <Badge className="bg-white/10 text-white/70 text-[9px] px-1.5 py-0 h-4">
                          {c.replyCount}
                        </Badge>
                      )}
                      {c.unread && (
                        <Badge className="bg-primary text-black text-[9px] uppercase px-1.5 py-0 h-4 min-w-[16px] justify-center">
                          {c.unreadCount || '•'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Coach thread modal (unchanged behavior, tighter styling) */}
      <AnimatePresence>
        {selectedConversation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex flex-col"
          >
            <div className="glass border-b border-border p-4">
              <div className="container mx-auto max-w-3xl flex items-center gap-3">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setSelectedConversation(null)}
                  className="h-8 w-8 p-0"
                  data-testid="close-thread-btn"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <h3 className="font-heading text-lg text-white uppercase tracking-wider">
                    {selectedConversation.title}
                  </h3>
                  <p className="text-muted-foreground text-xs">Conversation with your coach</p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4" data-testid="message-thread">
              <div className="container mx-auto max-w-3xl space-y-4">
                {loadingThread ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 text-primary animate-spin" />
                  </div>
                ) : (
                  <>
                    {thread.map((msg, i) => {
                      const palette = [
                        { bg: 'bg-sky-500/15',  border: 'border-sky-400/40',  name: 'text-sky-300' },
                        { bg: 'bg-primary/15',  border: 'border-primary/50',  name: 'text-primary' },
                        { bg: 'bg-white/10',    border: 'border-white/30',    name: 'text-white' },
                      ][i % 3];
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] ${palette.bg} ${palette.border} border rounded-2xl p-4`}>
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`h-6 w-6 rounded-full flex items-center justify-center ${
                                msg.sender_type === 'admin' ? 'bg-primary/20' : 'bg-white/10'
                              }`}>
                                {msg.sender_type === 'admin'
                                  ? <Shield className="h-3 w-3 text-primary" />
                                  : <User className="h-3 w-3 text-white/70" />}
                              </div>
                              <span className={`text-sm font-medium ${palette.name}`}>
                                {msg.sender_type === 'admin' ? 'Coach' : 'You'}
                              </span>
                              <span className="text-muted-foreground text-xs">
                                {formatFullDate(msg.created_at)}
                              </span>
                            </div>
                            <p className="text-white whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                            {msg.video_url && (
                              <div className="mt-3 pt-3 border-t border-border/50">
                                {msg.video_url.startsWith('/api/uploads') ? (
                                  <video
                                    src={`${API}${msg.video_url}`}
                                    controls
                                    className="w-full max-h-64 bg-black rounded"
                                  />
                                ) : (
                                  <a
                                    href={msg.video_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-primary hover:underline text-sm"
                                  >
                                    <Play className="h-4 w-4" /> Watch Video
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                    <div ref={threadEndRef} />
                  </>
                )}
              </div>
            </div>

            <div className="glass border-t border-border p-4">
              <div className="container mx-auto max-w-3xl">
                <div className="flex gap-3">
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type your reply..."
                    className="bg-black border-border text-white resize-none min-h-[60px]"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    data-testid="reply-input"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={sendingReply || !replyContent.trim()}
                    className="bg-primary text-black hover:bg-primary/90 px-6"
                    data-testid="send-reply-btn"
                  >
                    {sendingReply ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </Button>
                </div>
                <p className="text-muted-foreground text-[11px] mt-2">
                  Press Enter to send · Shift+Enter for new line
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessagesPage;
